import { Fragment, useEffect, useState } from 'react';
import { ROLE_DEFAULTS, SETTINGS_ROLES, type SettingsRole } from '@ehr/shared';
import { Modal } from '../components/Modal.js';
import { api } from '../lib/api.js';

/**
 * Access Rights Matrix (Settings mega-menu → Access Rights Matrix). A read-only
 * grid of roles × permission keys, from the org permission catalog crossed with
 * the role defaults (ROLE_DEFAULTS). Per-user grants/revokes layer on top and are
 * managed in Manage Users; this view shows the baseline each role gets.
 */
type Perm = { key: string; domain: string; label: string };
const ROLE_LABEL: Record<SettingsRole, string> = {
  admin: 'Admin',
  manager: 'Manager',
  clinician: 'Clinician',
  supervisor: 'Supervisor',
  front_desk: 'Front desk',
};

export function AccessMatrixModal({ onClose }: { onClose: () => void }) {
  const [perms, setPerms] = useState<Perm[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<Perm[]>('/permissions')
      .then((rows) => {
        setPerms(Array.isArray(rows) ? rows : []);
        setErr(null);
      })
      .catch(() => setErr('Could not load the permission catalog.'))
      .finally(() => setLoading(false));
  }, []);

  // Group permissions by domain for readable section rows.
  const byDomain = perms.reduce<Record<string, Perm[]>>((acc, p) => {
    (acc[p.domain] ??= []).push(p);
    return acc;
  }, {});
  const has = (role: SettingsRole, key: string) => ROLE_DEFAULTS[role]?.includes(key) ?? false;

  return (
    <Modal
      title="Access Rights Matrix"
      onClose={onClose}
      className="matrix-modal"
      actions={
        <button className="btn" onClick={onClose}>
          Close
        </button>
      }
    >
      <p className="muted config-sub">
        The default permissions each role receives. Per-user exceptions are set in Manage Users.
      </p>
      {err && <div className="config-err">{err}</div>}
      {loading ? (
        <p className="muted">Loading…</p>
      ) : perms.length === 0 ? (
        <p className="muted config-empty">No permission keys defined.</p>
      ) : (
        <div className="matrix-scroll">
          <table className="matrix-table">
            <thead>
              <tr>
                <th className="matrix-perm-head">Permission</th>
                {SETTINGS_ROLES.map((r) => (
                  <th key={r} className="matrix-role-head">
                    {ROLE_LABEL[r]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(byDomain).map(([domain, list]) => (
                <Fragment key={domain}>
                  <tr className="matrix-domain-row">
                    <td colSpan={SETTINGS_ROLES.length + 1}>{domain}</td>
                  </tr>
                  {list.map((p) => (
                    <tr key={p.key}>
                      <td className="matrix-perm">{p.label}</td>
                      {SETTINGS_ROLES.map((r) => (
                        <td key={r} className="matrix-cell">
                          {has(r, p.key) ? (
                            <span className="matrix-yes" aria-label="granted">
                              ✓
                            </span>
                          ) : (
                            <span className="matrix-no" aria-label="not granted">
                              –
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
